import { useCallback } from "react";
import { useNotificationPeek } from "@/components/peek/notification-peek-context";
import {
	actionSchemaSections,
	feedSchemaSections,
	type ActionSchemaEntry,
	type FeedSchemaEntry,
} from "@/lib/connectors/schema-sections";

/**
 * Shared "click a feed/action to see its schema in the peek pane" wiring.
 *
 * Every surface that lists what a connector brings in or can do — the connector
 * page's "What it can bring in" / "What agents can do", the create flow's
 * preview and configure steps, the connection page's feeds — opens the same
 * right-side schema peek from these two callbacks. One hook, three call sites.
 */
export function useConnectorSchemaPeek() {
	const peek = useNotificationPeek();

	const inspectFeed = useCallback(
		(key: string, feed: FeedSchemaEntry, href = "") => {
			if (!peek) return;
			peek.open({
				href,
				title: feed.name ?? key,
				schemaSections: feedSchemaSections(feed),
			});
		},
		[peek],
	);

	const inspectAction = useCallback(
		(key: string, action: ActionSchemaEntry) => {
			if (!peek) return;
			peek.open({
				href: "",
				title: action.name ?? key,
				schemaSections: actionSchemaSections(action),
			});
		},
		[peek],
	);

	return { inspectFeed, inspectAction };
}
